// IM-SAAS-005 (IM-UX-191/196): exibição dos recursos contratados com o uso do período — compartilhada entre a
// administração da plataforma e "Minha assinatura". Só exibe o que o servidor calculou; nenhuma regra comercial aqui.
import {
  BILLING_STATUS_LABEL, COMMERCIAL_STATUS_LABEL, CREDIT_KIND_LABEL, FEATURE_CATEGORIES, FEATURE_CATEGORY_LABEL, LIMIT_PERIOD_LABEL, USAGE_STATUS_LABEL,
  type CommercialStatus, type CreditLedgerEntryDto, type FeatureUsageDto, type SubscriptionEventDto, type UsageRecordDto,
} from '@intelimove/contracts';
import type { ReactNode } from 'react';
import '../styles/saas005.css';

const when = (v: string | null | undefined) => (v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export const COMMERCIAL_TAG: Record<CommercialStatus, string> = { LEGADO: 'gray', PENDENTE: 'amber', AVALIACAO: 'amber', ATIVA: 'mint', SUSPENSA: 'critical', CANCELADA: 'critical', EXPIRADA: 'critical' };
export const CommercialStatusTag = ({ status }: { status: CommercialStatus }) => <span className={`tag ${COMMERCIAL_TAG[status]}`}>{COMMERCIAL_STATUS_LABEL[status]}</span>;

/** Texto da regra efetiva de uma funcionalidade (uso do período quando há limite). */
export function ruleText(f: FeatureUsageDto): string {
  if (f.mode === 'INDISPONIVEL') return 'Não incluído';
  if (f.mode === 'CREDITOS') return `${f.credits ?? '—'} crédito(s) por uso`;
  if (f.mode === 'LIMITADO') return `${f.limit ?? 0} ${f.period ? LIMIT_PERIOD_LABEL[f.period] : ''}`.trim();
  return f.kind === 'OPERACAO' ? 'Sem limite (medido)' : 'Incluído';
}

export function usageText(f: FeatureUsageDto): string {
  if (f.mode === 'LIMITADO' && f.used !== null) return `${f.used} de ${f.limit ?? 0}${f.remaining !== null ? ` · ${f.remaining} disponível(is)` : ''}`;
  if (f.kind === 'CAPACIDADE' && f.used !== null) return `${f.used} em uso`;
  return '—';
}

export function FeatureUsageTable({ features, caption }: { features: FeatureUsageDto[]; caption: string }) {
  return (
    <div className="table-wrap"><table className="data-table stack cm-features">
      <caption className="sr-only">{caption}</caption>
      <thead><tr><th scope="col">Recurso</th><th scope="col">Condição</th><th scope="col">Uso no período</th><th scope="col">Renovação</th></tr></thead>
      {FEATURE_CATEGORIES.map((cat) => {
        const rows = features.filter((f) => f.category === cat);
        if (!rows.length) return null;
        return (
          <tbody key={cat}>
            <tr className="cm-group"><th scope="rowgroup" colSpan={4}>{FEATURE_CATEGORY_LABEL[cat]}</th></tr>
            {rows.map((f) => (
              <tr key={f.key} className={f.mode === 'INDISPONIVEL' ? 'cm-off' : ''}>
                <td data-label="Recurso"><b>{f.label}</b>{!f.instrumented && <span className="cell-sub">Medição em preparação</span>}</td>
                <td data-label="Condição">{ruleText(f)}{f.origin === 'LEGADO' && f.mode !== 'INDISPONIVEL' && <span className="cell-sub">Condição atual preservada</span>}</td>
                <td data-label="Uso no período">{usageText(f)}</td>
                <td data-label="Renovação">{f.renewsAt ? when(f.renewsAt) : f.mode === 'LIMITADO' && f.period !== 'SIMULTANEO' ? 'Diária' : '—'}</td>
              </tr>))}
          </tbody>
        );
      })}
    </table></div>
  );
}

export function UsageTable({ items, caption, showUser = true, action }: { items: UsageRecordDto[]; caption: string; showUser?: boolean; action?: (u: UsageRecordDto) => ReactNode }) {
  return (
    <div className="table-wrap"><table className="data-table stack">
      <caption className="sr-only">{caption}</caption>
      <thead><tr><th scope="col">Quando</th><th scope="col">Operação</th><th scope="col">Situação</th><th scope="col">Créditos</th>{showUser && <th scope="col">Solicitante</th>}{action && <th scope="col"><span className="sr-only">Ações</span></th>}</tr></thead>
      <tbody>{items.map((u) => (
        <tr key={u.id}>
          <td data-label="Quando">{when(u.createdAt)}<span className="cell-sub">{u.source === 'AGENDADA' ? 'Rotina agendada' : u.source === 'SISTEMA' ? 'Sistema' : 'Pedido de usuário'}</span></td>
          <td data-label="Operação">{u.featureLabel}<span className="cell-sub">{u.quantity} {u.unit === 'LOTE' ? 'lote(s)' : 'execução(ões)'}</span></td>
          <td data-label="Situação">{USAGE_STATUS_LABEL[u.status] ?? u.status}<span className="cell-sub">{BILLING_STATUS_LABEL[u.billingStatus] ?? u.billingStatus}</span></td>
          <td data-label="Créditos">{u.creditsCharged ? `${u.creditsCharged} consumido(s)` : u.creditsReserved && u.billingStatus === 'RESERVADO' ? `${u.creditsReserved} reservado(s)` : '—'}</td>
          {showUser && <td data-label="Solicitante">{u.userName ?? '—'}</td>}
          {action && <td data-label="Ações">{action(u)}</td>}
        </tr>))}</tbody>
    </table></div>
  );
}

export function LedgerTable({ items, caption }: { items: CreditLedgerEntryDto[]; caption: string }) {
  return (
    <div className="table-wrap"><table className="data-table stack">
      <caption className="sr-only">{caption}</caption>
      <thead><tr><th scope="col">Quando</th><th scope="col">Movimento</th><th scope="col">Quantidade</th><th scope="col">Validade</th><th scope="col">Origem</th></tr></thead>
      <tbody>{items.map((l) => (
        <tr key={l.id}>
          <td data-label="Quando">{when(l.at)}</td>
          <td data-label="Movimento">{CREDIT_KIND_LABEL[l.kind]}{l.reason && <span className="cell-sub">“{l.reason}”</span>}</td>
          <td data-label="Quantidade"><b className={l.amount < 0 ? 'cm-neg' : 'cm-pos'}>{l.amount > 0 ? `+${l.amount}` : l.amount}</b></td>
          <td data-label="Validade">{l.expiresAt ? when(l.expiresAt) : '—'}</td>
          <td data-label="Origem">{l.actorScope === 'SISTEMA' ? 'Sistema' : l.actorName ?? 'Administração da plataforma'}</td>
        </tr>))}</tbody>
    </table></div>
  );
}

const EVENT_LABEL: Record<string, string> = {
  LEGADO_APLICADO: 'Condição legado preservada', ASSINATURA_ALTERADA: 'Assinatura alterada', CREDITOS_AJUSTADOS: 'Créditos ajustados', CREDITOS_ESTORNADOS: 'Créditos estornados',
};

export function SubscriptionHistory({ items }: { items: SubscriptionEventDto[] }) {
  if (!items.length) return <p className="card-sub">Nenhuma alteração registrada.</p>;
  return (
    <ul className="cm-events">{items.map((e) => (
      <li key={e.id}>
        <time dateTime={e.at}>{when(e.at)}</time>
        <div><b>{EVENT_LABEL[e.kind] ?? e.kind}</b>
          {e.kind === 'ASSINATURA_ALTERADA' && <> · {e.fromPlan ?? 'Legado'} → {e.toPlan ?? 'Legado'}{e.fromStatus !== e.toStatus && e.toStatus ? ` (${COMMERCIAL_STATUS_LABEL[e.toStatus as CommercialStatus] ?? e.toStatus})` : ''}
            {e.fromSeats !== e.toSeats && <> · usuários {e.fromSeats ?? 'do plano'} → {e.toSeats ?? 'do plano'}</>}</>}
          <span className="cell-sub">{e.actorScope === 'SISTEMA' ? 'Registro do sistema' : `por ${e.actorName ?? 'Administração da plataforma'}`}{e.reason ? ` · “${e.reason}”` : ''}</span></div>
      </li>))}</ul>
  );
}
